import { useState } from "react";
import { Sun, Coffee, Dumbbell, Moon } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";

const moments = [
  {
    icon: Sun,
    time: "Morning",
    state: "Focus",
    label: "Morning focus",
    description: "Start your day sharp. A Focus snack with your coffee sets the tone.",
    detail: "L-Theanine + Lion's Mane work together to sharpen your attention without the jitters.",
    bg: "bg-[hsl(var(--pastel-yellow))]",
    textColor: "text-foreground",
  },
  {
    icon: Coffee,
    time: "Afternoon",
    state: "Chill",
    label: "Afternoon reset",
    description: "Beat the 3pm slump. A Chill snack brings calm clarity back.",
    detail: "Ashwagandha + Magnesium help your nervous system settle without drowsiness.",
    bg: "bg-[hsl(var(--pastel-blue))]",
    textColor: "text-foreground",
  },
  {
    icon: Dumbbell,
    time: "Pre-workout",
    state: "Energy",
    label: "Pre-workout energy",
    description: "Clean lift before you train. Sustained, not spiked.",
    detail: "Natural caffeine + B-vitamins deliver steady energy that lasts through your last set.",
    bg: "bg-[hsl(var(--pastel-peach))]",
    textColor: "text-foreground",
  },
  {
    icon: Moon,
    time: "Evening",
    state: "Sleep",
    label: "Evening wind-down",
    description: "Signal your body it's time. A Sleep snack to power down naturally.",
    detail: "Magnesium glycinate + Passionflower ease your mind and muscles into rest mode.",
    bg: "bg-[hsl(var(--pastel-lavender))]",
    textColor: "text-foreground",
  },
];

const UseCases = () => {
  const { ref, isVisible } = useScrollAnimation();
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="bg-background py-24 md:py-32">
      <div ref={ref} className="mx-auto max-w-5xl px-6">
        <div className="text-center">
          <p
            className={cn(
              "text-[11px] font-bold uppercase tracking-[0.2em] text-foreground/50 transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
          >
            Built for your day
          </p>
          <h2
            className={cn(
              "mt-3 heading-bold text-3xl text-foreground sm:text-4xl lg:text-[3.5rem] lg:leading-[0.95] transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
            style={{ transitionDelay: "120ms" }}
          >
            When to reach for a State
          </h2>
        </div>

        {/* Simple card grid instead of complex circle */}
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {moments.map((moment, i) => {
            const isActive = activeIndex === i;
            return (
              <button
                key={moment.label}
                onClick={() => setActiveIndex(i)}
                className={cn(
                  `${moment.bg} rounded-3xl p-6 text-left transition-all duration-500 ease-out hover:-translate-y-1`,
                  isActive && "ring-2 ring-foreground/20 -translate-y-1",
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                )}
                style={{ transitionDelay: `${i * 100 + 250}ms` }}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-foreground/10">
                  <moment.icon className="h-5 w-5 text-foreground" strokeWidth={1.5} />
                </div>
                <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/50">
                  {moment.time}
                </p>
                <h3 className="mt-1 font-bold text-lg text-foreground">
                  {moment.label}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/60">
                  {moment.description}
                </p>
                <div
                  className={cn(
                    "grid transition-all duration-500 ease-out",
                    isActive ? "grid-rows-[1fr] opacity-100 mt-3" : "grid-rows-[0fr] opacity-0 mt-0"
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="text-sm leading-relaxed text-foreground/50 border-t border-foreground/10 pt-3">
                      {moment.detail}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UseCases;
